import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { TApi, RequestTopicOptions, RequestTypeOptions, TRoutes, TTicketType } from '../../shared/const'
import http from '../../shared/http'
import { errorText } from '../../shared/errorMessage'
import useDialogDismiss from '../../shared/useDialogDismiss'
import { askConfirm } from '../../shared/confirmDialog'
import { meldeAnfrageGesendet } from '../../shared/requestEvents'
import {
	hatInhalt as entwurfHatInhalt,
	liesEntwurf,
	speichereEntwurf,
	verwirfEntwurf
} from '../../shared/requestDraft'

import Btn from '../Btn'
import Lightbox from '../Lightbox'
import { PlainInput, PlainSelect, PlainTextarea } from '../PlainField'

import styles from './form.module.scss'

const MAX_ANHAENGE = 5
const MAX_MB = 5
const ERLAUBT = 'image/png,image/jpeg,application/pdf'

const MIN_BESCHREIBUNG = 20

/** Für den Angabenblock: die Rolle in Worten statt als Schlüssel. */
const ROLLEN_NAMEN = {
	STUDENT: 'Student',
	TEACHER: 'Teacher',
	SUPPORTER: 'Supporter'
}

/**
 * Der Browsername aus der Kennung.
 *
 * Bewusst grob: Es geht darum, ob jemand mit Safari oder Firefox unterwegs ist —
 * nicht um die dritte Stelle der Versionsnummer. Die vollständige Kennung geht
 * ohnehin mit und steht dem Entwicklungsteam zur Verfügung.
 */
const browserAusKennung = kennung => {
	if (!kennung) return '—'
	if (/edg\//i.test(kennung)) return 'Edge'
	if (/opr\/|opera/i.test(kennung)) return 'Opera'
	if (/firefox/i.test(kennung)) return 'Firefox'
	if (/chrome|chromium/i.test(kennung)) return 'Chrome'
	if (/safari/i.test(kennung)) return 'Safari'
	return kennung.split(' ')[0]
}

/** Was automatisch mitgeht. Steht aufklappbar im Formular, nichts davon heimlich. */
const sammleKontext = (pathname, titel) => ({
	path: pathname,
	view: titel,
	userAgent: window.navigator.userAgent.slice(0, 400),
	viewport: `${window.innerWidth}×${window.innerHeight}`,
	appVersion: process.env.REACT_APP_VERSION || ''
})

/**
 * „Wie können wir helfen?" — das Formular hinter dem Hilfe-Knopf.
 *
 * Bewusst kurz: Titel, Beschreibung, Art, Anhänge. Die Hürde entscheidet, ob
 * überhaupt gemeldet wird — alles Weitere (Dringlichkeit, Projekt, die interne
 * Art „Entscheidung") vergibt die Verwaltung beim Sichten.
 */
export default function RequestForm({ onClose }) {
	const { t } = useTranslation()
	const location = useLocation()
	const seitentitel = typeof document !== 'undefined' ? document.title : ''

	const [titel, setTitel] = useState('')
	const [beschreibung, setBeschreibung] = useState('')
	const [art, setArt] = useState(TTicketType.ISSUE)
	const [dateien, setDateien] = useState([])
	// Ein wiederhergestellter Entwurf soll sich bemerkbar machen — sonst wundert
	// man sich, warum das Formular nicht leer ist.
	const [wiederhergestellt, setWiederhergestellt] = useState(false)
	// Nach dem Absenden bleibt das Fenster stehen und bestätigt — siehe unten.
	const [gesendeteNummer, setGesendeteNummer] = useState(null)
	const [fehler, setFehler] = useState({})
	const [sendet, setSendet] = useState(false)
	const [kontextOffen, setKontextOffen] = useState(false)
	const [ueberZiel, setUeberZiel] = useState(false)
	const [grossIndex, setGrossIndex] = useState(null)
	const dateiFeld = useRef(null)

	/*
	 * Themen mit eigener Zuständigkeit (Skills4Retail, Anforderung #216) — nur
	 * die, die der Server dieser Rolle anbietet. Unterstützer:innen haben den
	 * Knopf auch, bekommen das Thema aber nicht.
	 */
	const angeboteneThemen = useSelector(state => state.user.profile?.requestTopics)

	// Die drei Arten in der Sprache der Person — die Werte bleiben, wie sie sind.
	// Dahinter die Themen: Sie stehen in derselben Auswahl, weil die Person
	// nur eine Frage beantwortet („Worum geht es?").
	const artOptionen = useMemo(() => {
		const themen = Array.isArray(angeboteneThemen) ? angeboteneThemen : []
		return [
			...RequestTypeOptions,
			...RequestTopicOptions.filter(option => themen.includes(option.value))
		].map(option => ({ ...option, label: t(option.labelKey) }))
	}, [t, angeboteneThemen])

	/*
	 * Ein Entwurf kann ein Thema tragen, das diese Rolle nicht (mehr) sieht —
	 * dann gilt die erste Art, statt einen unsichtbaren Wert abzuschicken.
	 */
	const gewaehlt = artOptionen.some(option => option.value === art) ? art : TTicketType.ISSUE
	const istThema = RequestTopicOptions.some(option => option.value === gewaehlt)

	/*
	 * Vorschaubilder entstehen im Browser (`createObjectURL`) und müssen wieder
	 * freigegeben werden — sonst hält der Browser jedes eingefügte Bild bis zum
	 * Neuladen im Speicher. Die Zuordnung hängt an der Datei selbst, nicht am
	 * Index: Wer das dritte von fünf Bildern entfernt, würde sonst die Vorschau
	 * aller folgenden verlieren.
	 */
	const vorschauen = useMemo(
		() =>
			dateien.map(datei => ({
				datei,
				istBild: datei.type.startsWith('image/'),
				url: datei.type.startsWith('image/') ? URL.createObjectURL(datei) : null
			})),
		[dateien]
	)

	useEffect(
		() => () => vorschauen.forEach(eintrag => eintrag.url && URL.revokeObjectURL(eintrag.url)),
		[vorschauen]
	)

	// Nur Bilder lassen sich vergrößert durchblättern; ein PDF öffnet der
	// Browser ohnehin selbst.
	const bilder = vorschauen
		.filter(eintrag => eintrag.istBild)
		.map(eintrag => ({ src: eintrag.url, caption: eintrag.datei.name }))

	/*
	 * Der Entwurf liegt in IndexedDB und kommt deshalb erst nach dem ersten
	 * Anzeigen an. Was in der Zwischenzeit getippt wurde, gewinnt: Sonst
	 * überschriebe ein spät eintreffender alter Entwurf frische Eingaben.
	 */
	useEffect(() => {
		let aktuell = true

		liesEntwurf().then(gespeichert => {
			if (!aktuell || !entwurfHatInhalt(gespeichert)) return

			setTitel(vorher => vorher || gespeichert.titel || '')
			setBeschreibung(vorher => vorher || gespeichert.beschreibung || '')
			setArt(vorher => (vorher === TTicketType.ISSUE ? gespeichert.art || vorher : vorher))
			setDateien(vorher => (vorher.length ? vorher : gespeichert.dateien || []))
			setWiederhergestellt(true)
		})

		return () => {
			aktuell = false
		}
	}, [])

	const kontext = useMemo(
		() => sammleKontext(location.pathname, seitentitel),
		[location.pathname, seitentitel]
	)

	const schule = useSelector(state => state.user.profile?.school)
	const rolle = useSelector(state => state.user.role)

	/*
	 * Rolle und Browser in lesbarer Form. Die volle Kennung des Browsers ist
	 * eine Zeile Kauderwelsch — sie geht mit, aber angezeigt wird der Name.
	 */
	const rollenName = ROLLEN_NAMEN[rolle] ? t(ROLLEN_NAMEN[rolle]) : rolle
	const browserName = useMemo(() => browserAusKennung(kontext.userAgent), [kontext.userAgent])

	const etwasEingetragen = Boolean(titel.trim() || beschreibung.trim() || dateien.length)

	/** Alles, was den Entwurf ausmacht — die Anhänge eingeschlossen. */
	const alsEntwurf = () => ({ titel, beschreibung, art, dateien })

	/*
	 * Schließen heißt **behalten**, nicht verwerfen. Wer das Fenster zumacht, um
	 * ein Bildschirmfoto von einer anderen Seite zu holen, soll nicht gefragt
	 * werden, ob er seine Arbeit wegwerfen will — und erst recht nicht aus
	 * Versehen ja sagen. Weggeworfen wird nur über „Verwerfen".
	 */
	const behalteUndSchliesse = async () => {
		if (sendet) return

		if (etwasEingetragen) {
			await speichereEntwurf(alsEntwurf())
			toast.info(t('Saved as a draft — the help button brings it back.'))
		} else {
			await verwirfEntwurf()
		}

		onClose()
	}

	const verwerfen = async () => {
		if (sendet) return

		if (etwasEingetragen) {
			const sicher = await askConfirm({
				title: t('Discard draft?'),
				text: t('Text and attachments will be lost.'),
				confirmLabel: t('Discard'),
				tone: 'danger'
			})
			if (!sicher) return
		}

		await verwirfEntwurf()
		onClose()
	}

	const backdropProps = useDialogDismiss(behalteUndSchliesse, { enabled: !sendet })

	/**
	 * Eine Stelle für alle drei Wege: auswählen, einfügen (Strg+V), hereinziehen.
	 *
	 * Ein Bildschirmfoto liegt beim Melden fast immer schon in der
	 * Zwischenablage — es gerade erst gemacht zu haben ist ja der Grund, warum
	 * jemand meldet. Es vorher irgendwohin speichern zu müssen, nur um es dann
	 * wieder zu suchen, ist der Umweg, an dem eine Meldung scheitert.
	 */
	const nimmDateien = eingehend => {
		const neue = [...eingehend].filter(Boolean)
		if (!neue.length) return

		const erlaubt = ERLAUBT.split(',')
		const falscheArt = neue.find(datei => !erlaubt.includes(datei.type))
		if (falscheArt) {
			toast.error(t('Only PNG, JPEG and PDF are possible.'))
			return
		}

		const zuGross = neue.find(datei => datei.size > MAX_MB * 1024 * 1024)
		if (zuGross) {
			toast.error(
				t('„{{val1}}“ is larger than {{val2}} MB.', {
					val1: zuGross.name || t('Screenshot'),
					val2: MAX_MB
				})
			)
			return
		}

		if (dateien.length + neue.length > MAX_ANHAENGE) {
			toast.info(t('At most {{val1}} files are possible.', { val1: MAX_ANHAENGE }))
		}
		setDateien([...dateien, ...neue].slice(0, MAX_ANHAENGE))
	}

	const waehleDateien = event => {
		nimmDateien(event.target.files)
		// Zurücksetzen, sonst lässt sich dieselbe Datei nicht ein zweites Mal
		// wählen — der Wert ändert sich dann nicht.
		event.target.value = ''
	}

	/*
	 * Aus der Zwischenablage eingefügte Bildschirmfotos haben je nach
	 * Betriebssystem keinen oder einen nichtssagenden Namen („image.png"). Ein
	 * gezählter Name ist besser als fünfmal derselbe.
	 */
	const einfuegen = event => {
		const ausZwischenablage = [...(event.clipboardData?.files || [])]
		if (!ausZwischenablage.length) return

		// Nur wenn wirklich Dateien dabei sind — sonst würde jedes eingefügte
		// Textstück das Einfügen ins Textfeld verhindern.
		event.preventDefault()

		nimmDateien(
			ausZwischenablage.map((datei, index) =>
				datei.name && datei.name !== 'image.png'
					? datei
					: new File([datei], `Bildschirmfoto-${dateien.length + index + 1}.png`, {
							type: datei.type
					  })
			)
		)
	}

	const pruefe = () => {
		const naechst = {}
		if (titel.trim().length < 3) {
			naechst.titel = t('Please say in one sentence what this is about.')
		}
		if (beschreibung.trim().length < MIN_BESCHREIBUNG) {
			naechst.beschreibung = t(
				'Please describe briefly what happened (at least {{val1}} characters).',
				{ val1: MIN_BESCHREIBUNG }
			)
		}
		setFehler(naechst)
		return Object.keys(naechst).length === 0
	}

	const sende = async event => {
		event.preventDefault()
		if (sendet || !pruefe()) return

		setSendet(true)
		try {
			const { data } = await http.post(
				TApi.REQUESTS,
				{
					title: titel.trim(),
					description: beschreibung.trim(),
					// Ein Thema geht als „Klärungsbedarf" hinaus; Zuständigkeit und
					// Projekt setzt der Server.
					type: istThema ? TTicketType.OTHER : gewaehlt,
					topic: istThema ? gewaehlt : null,
					schoolId: schule?.id ?? null,
					context: kontext
				},
				{ disableToast: true }
			)

			/*
			 * Die Anhänge gehen einzeln hinterher: Die Anfrage ist damit auch dann
			 * angekommen, wenn eine Datei scheitert — und das ist die richtige
			 * Reihenfolge. Eine verlorene Datei lässt sich nachreichen, ein
			 * verlorener Text nicht.
			 */
			for (const datei of dateien) {
				const form = new FormData()
				form.append('attachment', datei)
				// eslint-disable-next-line no-await-in-loop
				await http.post(TApi.REQUEST_ATTACHMENTS(data.id), form, { disableToast: true })
			}

			await verwirfEntwurf()

			/*
			 * Kein Kurzhinweis, der nach fünf Sekunden weg ist: Wer gerade etwas
			 * abgeschickt hat, will wissen, ob es angekommen ist, wo es liegt und
			 * wann eine Antwort kommt. Genau diese drei Dinge stehen jetzt da —
			 * und die Nummer, die sich später zitieren lässt.
			 */
			/*
			 * „Meine Anfragen" kann offen hinter diesem Fenster liegen — dann
			 * stünde dort weiter „Sie haben noch keine Anfrage gestellt".
			 */
			meldeAnfrageGesendet(data.id)
			setGesendeteNummer(data.id)
		} catch (error) {
			toast.error(errorText(error, t('The request could not be sent.')))
			// Der Entwurf bleibt vollständig stehen — samt Anhängen.
			await speichereEntwurf(alsEntwurf())
		} finally {
			setSendet(false)
		}
	}

	/*
	 * Nach dem Absenden: dieselbe Fläche, anderer Inhalt. Ein eigenes Fenster
	 * über dem Formular wäre ein Fenster zu viel — und das Formular dahinter
	 * hätte nichts mehr zu sagen.
	 */
	if (gesendeteNummer !== null) {
		return (
			<div className={styles.overlay} {...backdropProps}>
				<div
					className={clsx(styles.dialog, styles.dialogNarrow)}
					role="dialog"
					aria-modal="true"
					aria-labelledby="hilfe-danke"
				>
					<div className={styles.doneBody}>
						<span className={styles.doneMark} aria-hidden="true">
							<svg viewBox="0 0 24 24">
								<path
									d="M4 12.5l5.5 5.5L20 7"
									fill="none"
									stroke="currentColor"
									strokeWidth="2.4"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>

						<h3 className={styles.doneTitle} id="hilfe-danke">
							{t('Thank you — your request has arrived.')}
						</h3>

						<p className={styles.doneNumber}>
							{t('It has the number #{{val1}}.', { val1: gesendeteNummer })}
						</p>

						<p className={styles.doneText}>
							{t(
								'We will look into it as soon as possible. You will find it — and the answer — under „My requests", and you will receive a message as soon as there is news.'
							)}
						</p>

						<div className={styles.doneActions}>
							{/*
							 * Mit der Nummer im Verweis: Wer ihm folgt, sucht die eben
							 * abgeschickte Anfrage nicht in einer Liste — sie geht gleich
							 * auf. Und die Adresse ändert sich, auch wenn die Seite schon
							 * offen war.
							 */}
							<Link
								className={styles.doneLink}
								to={`${TRoutes.MY_REQUESTS}?nr=${gesendeteNummer}`}
								onClick={onClose}
							>
								{t('To my requests')}
							</Link>
							<Btn type="button" onClick={onClose}>
								{t('Close')}
							</Btn>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.overlay} {...backdropProps} onPaste={einfuegen}>
			<div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="hilfe-titel">
				<div className={styles.header}>
					<h3 className={styles.title} id="hilfe-titel">
						{t('How can we help?')}
					</h3>
					<button
						type="button"
						className={styles.close}
						aria-label={t('Close')}
						onClick={behalteUndSchliesse}
					>
						<svg viewBox="0 0 12 12" aria-hidden="true">
							<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" />
						</svg>
					</button>
				</div>

				<form className={styles.body} onSubmit={sende}>
					{wiederhergestellt && (
						<p className={styles.restored}>
							{t('Your saved draft has been restored.')}
						</p>
					)}

					<PlainSelect
						label={t('What is it about?')}
						value={gewaehlt}
						options={artOptionen}
						onChange={event => setArt(event.target.value)}
					/>

					<PlainInput
						label={t('In short')}
						required
						placeholder={t('e.g. „Invoice will not open“')}
						value={titel}
						error={fehler.titel}
						maxLength={200}
						onChange={event => setTitel(event.target.value)}
					/>

					<PlainTextarea
						label={t('What happened?')}
						required
						rows={6}
						placeholder={t('What did you do, what happened, and what did you expect?')}
						value={beschreibung}
						error={fehler.beschreibung}
						maxLength={5000}
						onChange={event => setBeschreibung(event.target.value)}
					/>

					{/*
					 * Ablagefläche statt Knopf: Ziehen, Einfügen und Auswählen führen
					 * alle hierher, und sie sagt selbst, dass es drei Wege gibt.
					 */}
					<div
						className={clsx(styles.dropzone, ueberZiel && styles.dropzoneOver)}
						onDragOver={event => {
							event.preventDefault()
							setUeberZiel(true)
						}}
						onDragLeave={() => setUeberZiel(false)}
						onDrop={event => {
							event.preventDefault()
							setUeberZiel(false)
							nimmDateien(event.dataTransfer?.files || [])
						}}
					>
						<button
							type="button"
							className={styles.fileBtn}
							onClick={() => dateiFeld.current?.click()}
							disabled={dateien.length >= MAX_ANHAENGE}
						>
							{t('Attach a screenshot or PDF')}
						</button>
						<span className={styles.fileHint}>
							{t('or drag it here or paste it with Ctrl+V')} ·{' '}
							{t('up to {{val1}} files, {{val2}} MB each', { val1: MAX_ANHAENGE, val2: MAX_MB })}
						</span>
						<input
							ref={dateiFeld}
							type="file"
							accept={ERLAUBT}
							multiple
							hidden
							onChange={waehleDateien}
						/>
					</div>

					<div className={styles.files}>
						{/*
						 * Kacheln statt einer Dateiliste: Bei einem Bildschirmfoto
						 * sagt der Dateiname nichts („Bildschirmfoto 2026-08-23 um
						 * 09.14.22"), das Bild alles. Ein Klick zeigt es groß, die
						 * Pfeiltasten blättern durch alle — dieselbe Bedienung wie
						 * bei den Dateien der Anforderung.
						 */}
						{vorschauen.length > 0 && (
							<ul className={styles.thumbs}>
								{vorschauen.map((eintrag, index) => {
									const bildIndex = vorschauen
										.filter(x => x.istBild)
										.findIndex(x => x.datei === eintrag.datei)

									return (
										<li className={styles.thumb} key={`${eintrag.datei.name}-${index}`}>
											{eintrag.istBild ? (
												<button
													type="button"
													className={styles.thumbOpen}
													title={t('Show larger')}
													aria-label={`${eintrag.datei.name} — ${t('Show larger')}`}
													onClick={() => setGrossIndex(bildIndex)}
												>
													<img
														className={styles.thumbImage}
														src={eintrag.url}
														alt={eintrag.datei.name}
													/>
												</button>
											) : (
												<span className={styles.thumbPdf}>PDF</span>
											)}

											<span className={styles.thumbName} title={eintrag.datei.name}>
												{eintrag.datei.name}
											</span>

											<button
												type="button"
												className={styles.thumbRemove}
												aria-label={`${eintrag.datei.name} — ${t('remove')}`}
												title={t('remove')}
												onClick={() =>
													setDateien(prev =>
														prev.filter(datei => datei !== eintrag.datei)
													)
												}
											>
												×
											</button>
										</li>
									)
								})}
							</ul>
						)}
					</div>

					{/*
					 * Was mitgeht, steht sichtbar da — aufklappbar, aber nicht
					 * versteckt. Es steht ebenso in der Datenschutzerklärung.
					 */}
					{/*
					 * Was mitgeht, steht sichtbar da — aufklappbar, aber nicht
					 * versteckt, und als Angabenblock und nicht als Fließtext:
					 * Wer wissen will, was er mitschickt, liest eine Liste, keinen
					 * Absatz.
					 */}
					<div className={styles.context}>
						<button
							type="button"
							className={styles.contextToggle}
							aria-expanded={kontextOffen}
							onClick={() => setKontextOffen(offen => !offen)}
						>
							<svg
								className={clsx(styles.chevron, kontextOffen && styles.chevronOpen)}
								viewBox="0 0 10 6"
								aria-hidden="true"
							>
								<path
									d="M1 1l4 4 4-4"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							{t('These details are sent along')}
						</button>

						{kontextOffen && (
							<div className={styles.contextBox}>
								<dl className={styles.contextList}>
									<dt>{t('Page')}</dt>
									<dd className={styles.contextValue}>{kontext.path}</dd>

									<dt>{t('View')}</dt>
									<dd>{kontext.view || '—'}</dd>

									<dt>{t('Role')}</dt>
									<dd>{rollenName}</dd>

									<dt>{t('Browser')}</dt>
									<dd>{browserName}</dd>

									<dt>{t('Window')}</dt>
									<dd className={styles.contextValue}>{kontext.viewport}</dd>
								</dl>

								<p className={styles.contextNote}>
									{t(
										'Junior Company or school are added automatically, so that the report can be retraced.'
									)}
								</p>
							</div>
						)}
					</div>

					<p className={styles.hint}>
						{t(
							'Please do not include passwords. For questions about your company, your teacher is the first port of call.'
						)}
					</p>

					<div className={styles.footer}>
						{/*
						 * Drei Wege statt zweier. „Später weiter" ist der Grund,
						 * warum es sie gibt: Wer ein Bildschirmfoto von einer
						 * anderen Seite holen will, muss dieses Fenster verlassen —
						 * und danach alles wiederfinden, Anhänge eingeschlossen.
						 */}
						<Btn theme="minimal" type="button" onClick={verwerfen} disabled={sendet}>
							{t('Discard')}
						</Btn>

						<span className={styles.footerSpacer} />

						<Btn
							theme="secondary"
							type="button"
							onClick={behalteUndSchliesse}
							disabled={sendet}
						>
							{t('Continue later')}
						</Btn>
						<Btn type="submit" disabled={sendet}>
							{sendet ? t('Sending …') : t('Send request')}
						</Btn>
					</div>
				</form>
			</div>

			{/*
			 * Vergrößert ansehen und mit den Pfeiltasten blättern — dieselbe
			 * Komponente wie im Anforderungsfenster der Verwaltung.
			 */}
			{grossIndex !== null && bilder[grossIndex] && (
				<Lightbox
					images={bilder}
					index={grossIndex}
					onIndexChange={setGrossIndex}
					onClose={() => setGrossIndex(null)}
				/>
			)}
		</div>
	)
}

RequestForm.propTypes = {
	onClose: PropTypes.func.isRequired
}
